// src/components/agents/AgentConversationsDialog.jsx
import { useEffect, useMemo, useState, useContext } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  List,
  ListItem,
  ListItemText,
  Checkbox,
  Chip,
  Stack,
  Typography,
  IconButton,
  Box,
  Tooltip,
} from "@mui/material";
import DeleteForeverIcon from "@mui/icons-material/DeleteForever";
import { db } from "../../firebase";
import {
  collection,
  query,
  orderBy,
  limit,
  onSnapshot,
  doc,
  getDoc,
  getDocs,
  deleteDoc,
} from "firebase/firestore";
import { useAuth } from "../../context/AuthContext";
import { LanguageContext } from "../../context/LanguageContext";
import { getApp } from "firebase/app";
import ConversationDetailDialog from "./ConversationDetailDialog";

export default function AgentConversationsDialog({ open, onClose, agent, onMarkSeen }) {
  const [items, setItems] = useState([]);
  const { currentUser } = useAuth();
  const { translate } = useContext(LanguageContext);
  const [selected, setSelected] = useState(null); // { id, ...data }
  const [toDelete, setToDelete] = useState(null); // { id, ...data }
  const [deleting, setDeleting] = useState(false);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkDeleting, setBulkDeleting] = useState(false);

  useEffect(() => {
    if (!open || !agent?.id) return;
    const app = getApp();
    console.log("[Convos] using projectId", app.options.projectId);

    (async () => {
      try {
        if (!currentUser?.uid) {
          console.warn("[Convos] no currentUser, you must be logged in");
        } else {
          const profRef = doc(db, "profilsUsers", currentUser.uid);
          const profSnap = await getDoc(profRef);
          console.log("[Convos] profile exists?", profSnap.exists());
        }
      } catch (e) {
        console.error("[Convos] profile read error (client)", e);
      }
    })();

    console.log("[Convos] open for agent", {
      agentId: agent.id,
      userUid: currentUser?.uid,
    });
    const ref = collection(db, "messages", `agent_${agent.id}`, "conversations");
    console.log("[Convos] listening path", `messages / agent_${agent.id} / conversations`);

    const q = query(ref, orderBy("updatedAt", "desc"), limit(50));
    const unsub = onSnapshot(
      q,
      (snap) => {
        const rows = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        setItems(rows);
      },
      (err) => {
        console.error("[Convos] snapshot ERROR", err);
      }
    );

    return unsub;
  }, [open, agent?.id, currentUser?.uid]);

  useEffect(() => {
    if (!open) {
      setSelectedIds(new Set());
      return;
    }
    const ids = new Set(items.map((i) => i.id));
    setSelectedIds((prev) => new Set([...prev].filter((id) => ids.has(id))));
  }, [open, items]);

  const allIds = items.map((i) => i.id);
  const allSelected = selectedIds.size > 0 && selectedIds.size === items.length;
  const someSelected = selectedIds.size > 0 && selectedIds.size < items.length;

  // Build a set of seen conversation IDs from localStorage for this agent
  const seenSet = useMemo(() => {
    try {
      const baseKey = `tm:seenConvos:${agent?.id}`;
      const baseArr = JSON.parse(localStorage.getItem(baseKey) || "[]");
      const s = new Set(Array.isArray(baseArr) ? baseArr : []);
      // Also consider tenant-specific keys if tenantId is present on conversations
      const tenantIds = Array.from(new Set(items.map((i) => i.tenantId).filter(Boolean)));
      tenantIds.forEach((tid) => {
        const k = `tm:seenConvos:${tid}:${agent?.id}`;
        const arr = JSON.parse(localStorage.getItem(k) || "[]");
        if (Array.isArray(arr)) arr.forEach((id) => s.add(id));
      });
      return s;
    } catch {
      return new Set();
    }
  }, [agent?.id, items, selected]);

  const toggleOne = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleAll = () => {
    if (allSelected) setSelectedIds(new Set());
    else setSelectedIds(new Set(allIds));
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="md"
      scroll="paper"
      PaperProps={{ sx: { maxHeight: '80vh' } }}
    >
      <DialogTitle sx={{ fontWeight: 700, display: 'flex', alignItems: 'center', gap: 1 }}>
        Conversations — {agent?.name}
        <Chip size="small" label={`${items.length} total`} sx={{ ml: 1 }} />
      </DialogTitle>
      <DialogContent dividers sx={{ maxHeight: '70vh', overflowY: 'auto', bgcolor: 'background.default' }}>
        {items.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            No conversations yet.
          </Typography>
        ) : (
          <List dense sx={{ pr: 0 }}>
            {items.map((c) => {
              const site = c?.userHints?.site || c?.userHints?.source || "unknown";
              const updated = c?.updatedAt?.seconds ? new Date(c.updatedAt.seconds * 1000).toLocaleString() : "";
              let isUnseen = false;
              try {
                const updatedSec = c?.updatedAt?.seconds || 0;
                const baseObj = JSON.parse(localStorage.getItem(`tm:seenConvosTS:${agent?.id}`) || "{}") || {};
                const tenObj = c.tenantId
                  ? JSON.parse(localStorage.getItem(`tm:seenConvosTS:${c.tenantId}:${agent?.id}`) || "{}") || {}
                  : {};
                const lastSeen = Number(tenObj[c.id] ?? baseObj[c.id] ?? 0);
                isUnseen = updatedSec > lastSeen;
              } catch {
                isUnseen = false;
              }
              return (
                <ListItem
                  key={c.id}
                  divider
                  secondaryAction={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {isUnseen && (
                        <Chip size="small" color="warning" label={translate("NewMessages") || "New messages"} />
                      )}
                      <IconButton
                        edge="end"
                        aria-label="delete"
                        onClick={(e) => {
                          e.stopPropagation();
                          setToDelete(c);
                        }}
                        color="error"
                      >
                        <DeleteForeverIcon />
                      </IconButton>
                    </Box>
                  }
                  button
                  onClick={() => {
                    setSelected(c);
                    try { onMarkSeen && onMarkSeen(c.id); } catch {}
                    // Mark last seen timestamp locally for immediate UI update
                    try {
                      const ts = c?.updatedAt?.seconds || Math.floor(Date.now()/1000);
                      const baseKeyTS = `tm:seenConvosTS:${agent?.id}`;
                      const baseObj = JSON.parse(localStorage.getItem(baseKeyTS) || "{}") || {};
                      baseObj[c.id] = ts;
                      localStorage.setItem(baseKeyTS, JSON.stringify(baseObj));
                      if (c.tenantId) {
                        const tk = `tm:seenConvosTS:${c.tenantId}:${agent?.id}`;
                        const obj = JSON.parse(localStorage.getItem(tk) || "{}") || {};
                        obj[c.id] = ts;
                        localStorage.setItem(tk, JSON.stringify(obj));
                      }
                    } catch {}
                  }}
                  sx={{
                    borderRadius: 1.5,
                    '&:hover': { backgroundColor: 'action.hover' },
                    cursor: 'pointer',
                    '& .MuiListItemText-root': { cursor: 'inherit' },
                    '& .MuiTypography-root': { cursor: 'inherit' },
                  }}
                >
                  <Checkbox
                    edge="start"
                    checked={selectedIds.has(c.id)}
                    tabIndex={-1}
                    disableRipple
                    onClick={(e) => e.stopPropagation()}
                    onChange={() => toggleOne(c.id)}
                    sx={{ mr: 1 }}
                  />
                  <ListItemText
                    primary={
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Typography variant="subtitle2" sx={{ mr: 1, fontWeight: 600 }}>
                          {c.id}
                        </Typography>
                        <Chip size="small" label={site} variant="outlined" />
                        {c?.userHints?.email && (
                          <Tooltip title={translate("SendEmail") || "Send email"} arrow>
                            <Chip
                              size="small"
                              label={c.userHints.email}
                              color="primary"
                              variant="outlined"
                              onClick={(e) => {
                                e.stopPropagation();
                                const url = `https://mail.google.com/mail/?view=cm&fs=1&to=${encodeURIComponent(c.userHints.email)}`;
                                window.open(url, "_blank", "noopener,noreferrer");
                              }}
                              sx={{ cursor: "pointer" }}
                            />
                          </Tooltip>
                        )}
                      </Stack>
                    }
                    secondary={`Interactions: ${c.interactions ?? 0}  •  Updated: ${updated}`}
                  />
                </ListItem>
              );
            })}
          </List>
        )}
      </DialogContent>
      {/* Conversation detail */}
      <ConversationDetailDialog
        open={Boolean(selected)}
        onClose={() => setSelected(null)}
        agentId={agent?.id}
        conversationId={selected?.id || null}
      />
      <DialogActions sx={{ justifyContent: 'space-between', px: 2, py: 1.5 }}>
        <Stack direction="row" alignItems="center" spacing={2}>
          <Checkbox indeterminate={someSelected} checked={allSelected} onChange={toggleAll} />
          <Typography variant="body2">{selectedIds.size} selected</Typography>
          <Button size="small" variant="text" onClick={toggleAll} sx={{ textTransform: 'none', color: 'text.secondary' }}>
            {allSelected ? 'Clear selection' : 'Select all'}
          </Button>
        </Stack>
        <Stack direction="row" spacing={1.2}>
          <Button
            color="error"
            variant="contained"
            disabled={selectedIds.size === 0 || bulkDeleting}
            onClick={async () => {
              if (!agent?.id || selectedIds.size === 0) return;
              setBulkDeleting(true);
              try {
                for (const convId of selectedIds) {
                  const convRef = doc(db, 'messages', `agent_${agent.id}`, 'conversations', convId);
                  const eventsRef = collection(convRef, 'events');
                  const snap = await getDocs(eventsRef);
                  for (const d of snap.docs) {
                    await deleteDoc(doc(eventsRef, d.id));
                  }
                  await deleteDoc(convRef);
                }
                setSelectedIds(new Set());
              } catch (e) {
                console.error('[Convos] bulk delete failed', e);
              } finally {
                setBulkDeleting(false);
              }
            }}
          >
            Delete selected
          </Button>
          <Button
            onClick={onClose}
            variant="outlined"
            sx={{
              borderColor: '#616161',
              color: '#E0E0E0',
              fontWeight: 600,
              textTransform: 'none',
              borderRadius: '8px',
              px: 2,
              '&:hover': { borderColor: '#9E9E9E', backgroundColor: 'rgba(255,255,255,0.04)' },
            }}
          >
            Close
          </Button>
        </Stack>
      </DialogActions>

      {/* Confirm delete */}
      <Dialog open={Boolean(toDelete)} onClose={() => (!deleting && setToDelete(null))} maxWidth="xs" fullWidth>
        <DialogTitle>Delete conversation</DialogTitle>
        <DialogContent>
          <Typography variant="body2">Are you sure you want to delete this conversation and its messages?</Typography>
        </DialogContent>
        <DialogActions>
          <Button disabled={deleting} onClick={() => setToDelete(null)}>Cancel</Button>
          <Button
            color="error"
            variant="contained"
            disabled={deleting}
            onClick={async () => {
              if (!agent?.id || !toDelete?.id) return;
              setDeleting(true);
              try {
                const convRef = doc(db, 'messages', `agent_${agent.id}`, 'conversations', toDelete.id);
                const eventsRef = collection(convRef, 'events');
                const snap = await getDocs(eventsRef);
                for (const d of snap.docs) {
                  await deleteDoc(doc(eventsRef, d.id));
                }
                await deleteDoc(convRef);
                setToDelete(null);
              } catch (e) {
                console.error('[Convos] delete failed', e);
              } finally {
                setDeleting(false);
              }
            }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  );
}





